/**
 * How a list page's filters cross between the URL and its controls.
 *
 * List pages keep their tab, filter and search-box selections in the URL, so a
 * browser back from a detail view lands on the page the user left rather than
 * a reset one (NAV-45). Each parser returns `undefined` for "not set" and the
 * router leaves undefined values out of the query string — so an untouched page
 * has a bare URL and only what the user actually chose ever shows up in it.
 */

/**
 * The "no filter" entry of a dropdown. Radix's Select rejects an empty string
 * as an item value, so an unset filter needs a sentinel to show; it stays out
 * of the URL, where absence says the same thing.
 *
 * `FilterSelect` is the only component that renders it. It lives here because
 * the parsers have to know the one value that means "unset" rather than
 * treating it as something to filter by.
 */
export const ALL = "ALL";

/**
 * A free-text filter. Empty or non-string input reads as unset.
 *
 * The value is handed back exactly as typed — a search box is a controlled
 * input reading the param it writes, so trimming here would rewrite the box
 * under the fingers of whoever is typing. Callers trim when they match.
 */
export function textParam(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * A filter with a fixed set of choices. Anything outside `options` — a stale
 * link, a hand-edited URL — reads as unset, so the list is never filtered by a
 * value none of its controls can show.
 */
export function optionParam<T extends string>(
  options: readonly T[],
  value: unknown,
): T | undefined {
  return options.includes(value as T) ? (value as T) : undefined;
}

/**
 * A filter whose choices are data rather than a fixed list — the business
 * units that exist, the actions the loaded logs happen to hold. There is
 * nothing to check the value against, so anything non-empty is accepted and a
 * value nobody holds simply filters to nothing, which is what the page says.
 *
 * The one value it must reject is the {@link ALL} sentinel: the dropdown would
 * show "All …" as selected while the list filtered on the literal string, so
 * the page would claim no filter and show an empty table.
 */
export function filterParam(value: unknown): string | undefined {
  const text = textParam(value);
  return text === ALL ? undefined : text;
}

/**
 * A comma inside one of the values, so the separator stays unambiguous.
 *
 * `%` goes first on the way out and last on the way back, which is what makes
 * the pair lossless: a value that already contains the literal text `%2C`
 * becomes `%252C` and comes back as itself, instead of decoding into a comma it
 * never had.
 *
 * Only these two characters are touched. Encoding the whole value would be
 * simpler and would cost the thing this parameter shape exists for — a URL
 * somebody can read in a ticket — so `?cat=Design,Analytics` still reads as
 * itself and only a name with a comma in it pays.
 */
function escapeSeparators(value: string): string {
  return value.replaceAll("%", "%25").replaceAll(",", "%2C");
}

function unescapeSeparators(value: string): string {
  return value.replaceAll("%2C", ",").replaceAll("%25", "%");
}

/**
 * A filter that can hold several data-driven values at once, carried as one
 * comma-separated parameter — `?bu=bu-chaptr,bu-holtzbrinck`.
 *
 * Comma-joined rather than a repeated or JSON-encoded parameter so the URL
 * stays something a reviewer can read in a ticket and edit by hand, which is
 * what these params are for.
 *
 * **A value containing the separator is escaped rather than assumed away.** This
 * used to say the cost was borne by nobody because "document ids here are
 * slugs" — true of the first caller (`bu`, business-unit ids) and false the
 * moment the group catalogue's category filter arrived (Jira NAV-109): a
 * category name is free text typed into a box on `/admin/categories`, so
 * `Legal, Risk & Compliance` split into two strings matching nothing, and the
 * control went on showing it as selected over an empty table. Escaping is in
 * here rather than at either call site because the split and the join have to
 * agree, and a hand-edited URL has to read the same as a generated one.
 *
 * Empties and duplicates are dropped, and so is the {@link ALL} sentinel, for
 * the reason `filterParam` gives: it would leave the control reading "All …"
 * while the list filtered on the literal string. An empty result is
 * `undefined`, so an untouched filter leaves nothing in the URL.
 */
export function listParam(value: unknown): string[] | undefined {
  const text = textParam(value);
  if (text === undefined) return undefined;
  const values = [
    ...new Set(
      text
        .split(",")
        // Trimmed before unescaping, so a name's own leading space survives an
        // escaped comma (`a%2C b` → `a, b`) while `?cat=a, b` still reads as two.
        .map((part) => unescapeSeparators(part.trim()))
        .filter((part) => part !== "" && part !== ALL),
    ),
  ];
  return values.length > 0 ? values : undefined;
}

/** The inverse of {@link listParam}: what to put in the URL for a selection,
 *  and `undefined` for an empty one so the param disappears rather than
 *  lingering as `?bu=`. */
export function listValue(values: readonly string[]): string | undefined {
  return values.length > 0
    ? values.map(escapeSeparators).join(",")
    : undefined;
}
